import { Button } from '@invent/wl-ui-kit-next/ui';
import { LoaderCircle } from 'lucide-react';

import type { ILookupNote } from './use-response';

/**
 * What a CRD lookup said, beside the CRD it was asked about (SR-416, B6-3, ML §6).
 *
 * The session's alone: survey-api keeps the names it proposed and their provenance, never the
 * registries' status or a member's firm (ML §6, "the state document does not list pins"), so after
 * a reload there is no note and the names' own strip says "From BrokerCheck". The words are this
 * app's — survey-api answers a status and a 422 reason, not sentences.
 *
 * `data-lookup` carries the status (`ok`, `not_found`, `error`, `refused`, `failed`, `pending`): a spec
 * asserting on "Found:" would be asserting on English.
 */

/** A 422 reason → the sentence beside the CRD; `null` says nothing (the field is not shown). */
const REFUSED_WORDS: Record<string, Nullable<string>> = {
  crd_missing: 'Enter the CRD number first.',
  crd_invalid: 'A CRD number is 1 to 10 digits.',
  lookup_not_offered: null
};

export function lookupWords(note: ILookupNote): Nullable<string> {
  switch (note.status) {
    case 'pending':
      return 'Looking this CRD up…';
    case 'ok': {
      const name = [note.firstName, note.lastName].filter(Boolean).join(' ');
      if (!name) return 'Found in the registries.';
      return note.firmName ? `Found: ${name}, ${note.firmName}` : `Found: ${name}`;
    }
    case 'not_found':
      return 'No one with this CRD in the registries.';
    case 'error':
      return 'The registries did not answer — type the names yourself.';
    case 'failed':
      return 'Could not look this CRD up — type the names yourself.';
    case 'refused':
      return note.reason !== undefined && note.reason in REFUSED_WORDS
        ? REFUSED_WORDS[note.reason]
        : (note.reason ?? null);
    default:
      return null;
  }
}

/**
 * The note under a CRD, and — on a member row whose names a lookup left — "No, not this person",
 * which undoes that lookup (an unlink, the survey review's G10); the caller decides where it is
 * offered (`lookupAside`).
 */
export function LookupAside({
  note,
  crd,
  onNotThisPerson
}: {
  note: Nullable<ILookupNote>;
  /** The CRD on screen, so a note about another CRD — one typed or cleared since — is not drawn. */
  crd: string;
  onNotThisPerson?: () => void;
}) {
  const shown = note && (note.status === 'pending' || note.crd === crd.trim()) ? note : null;
  const words = shown ? lookupWords(shown) : null;
  if (!words && !onNotThisPerson) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
      {shown && words && (
        <p
          className="flex items-center gap-1.5 text-sm text-[var(--color-royal-blue)]"
          data-lookup={shown.status}
          role="status"
        >
          {shown.status === 'pending' && (
            <LoaderCircle className="size-3.5 animate-spin" aria-hidden />
          )}
          {words}
        </p>
      )}
      {onNotThisPerson && (
        <Button type="button" size="sm" variant="ghost" onClick={onNotThisPerson}>
          No, not this person
        </Button>
      )}
    </div>
  );
}
